import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useScrollLock from '../../hooks/useScrollLock';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSalon } from '../../contexts/SalonContext';
import { SUPPORTED_CURRENCIES, getDefaultCurrencyForLanguage, getDefaultConversionRate } from '../../utils/currency';
import { generatePriceListPdf } from '../../utils/generatePriceListPdf';
import './Salon.css';

const getTodayDateInputValue = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const DownloadPriceListModal = ({ isOpen, onClose, salonId, salonName, canIncludeZeroStock = false }) => {
  const { t, language, formatDate } = useLanguage();
  const { currency: dbCurrency } = useSalon();
  const [targetCurrency, setTargetCurrency] = useState('EUR');
  const [conversionRate, setConversionRate] = useState('1');
  const [includeZeroStock, setIncludeZeroStock] = useState(false);
  const [validFromDate, setValidFromDate] = useState(getTodayDateInputValue);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  useScrollLock(isOpen);

  const sameCurrency = targetCurrency === dbCurrency;

  useEffect(() => {
    if (isOpen) {
      const defaultTarget = getDefaultCurrencyForLanguage(language);
      setTargetCurrency(defaultTarget);
      setConversionRate(getDefaultConversionRate(dbCurrency, defaultTarget));
      setIncludeZeroStock(false);
      setValidFromDate(getTodayDateInputValue());
      setError('');
    }
  }, [isOpen, language, dbCurrency]);

  const handleTargetCurrencyChange = (code) => {
    setTargetCurrency(code);
    setConversionRate(getDefaultConversionRate(dbCurrency, code));
  };

  const handleDownload = async () => {
    const rate = parseFloat(conversionRate);
    if (!sameCurrency && (Number.isNaN(rate) || rate <= 0)) {
      setError(t('dresses.priceListInvalidRate', 'Conversion rate must be greater than 0.'));
      return;
    }

    try {
      setGenerating(true);
      setError('');

      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/dresses/price-list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          include_zero_stock: canIncludeZeroStock && includeZeroStock ? 'true' : 'false',
        },
      });

      const dresses = response.data.dresses || [];
      if (dresses.length === 0) {
        setError(t('dresses.priceListNoDresses', 'No dresses match the selected options.'));
        return;
      }

      const effectiveRate = sameCurrency ? 1 : rate;
      const [year, month, day] = validFromDate.split('-').map(Number);
      const formattedValidFromDate = formatDate(new Date(year, month - 1, day), { includeYear: true });

      await generatePriceListPdf({
        dresses,
        salonId,
        salonHeading: t('dresses.priceListSalonName', '{name} Bridal Shop', { name: salonName || '' }),
        title: t('dresses.priceListTitle', 'Price List'),
        validFromText: t('dresses.priceListValidFrom', 'Valid from: {date}', { date: formattedValidFromDate }),
        columns: {
          index: t('dresses.priceListIndex', '#'),
          name: t('dresses.dressName'),
          itemCode: t('dresses.itemCode'),
          rentalPrice: t('dresses.rentalPrice'),
          sellingPrice: t('dresses.sellingPrice'),
        },
        formatDressName: (name) => t('dresses.priceListDressName', '{name}', { name }),
        targetCurrency,
        conversionRate: effectiveRate,
        language,
      });

      onClose();
    } catch (err) {
      console.error('Error generating price list PDF:', err);
      setError(
        err.message
        || err.response?.data?.error
        || t('messages.error.failedToGeneratePriceList', 'Failed to generate price list PDF')
      );
    } finally {
      setGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '480px' }}>
        <div className="modal-header">
          <h3>{t('dresses.priceListModalTitle', 'Download price list')}</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="price-list-currency">{t('dresses.priceListCurrency', 'Currency')}</label>
            <select
              id="price-list-currency"
              value={targetCurrency}
              onChange={(e) => handleTargetCurrencyChange(e.target.value)}
              disabled={generating}
            >
              {SUPPORTED_CURRENCIES.map(({ code, label }) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="price-list-conversion-rate">
              {t('dresses.priceListConversionRate', 'Conversion rate')}
            </label>
            <input
              id="price-list-conversion-rate"
              type="number"
              min="0"
              step="any"
              value={conversionRate}
              onChange={(e) => setConversionRate(e.target.value)}
              disabled={generating || sameCurrency}
            />
            <p className="form-hint">
              {sameCurrency
                ? t('dresses.priceListSameCurrencyHint', 'Conversion rate is not needed when the list uses the same currency as the salon.')
                : t(
                  'dresses.priceListConversionRateHint',
                  'Multiply prices in {dbCurrency} by this rate to get {targetCurrency}.',
                  { dbCurrency, targetCurrency }
                )}
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="price-list-valid-from">
              {t('dresses.priceListValidFromDate', 'Valid from date')}
            </label>
            <div className="date-field-wrap">
              <input
                id="price-list-valid-from"
                type="date"
                value={validFromDate}
                onChange={(e) => setValidFromDate(e.target.value)}
                disabled={generating}
              />
            </div>
          </div>

          {canIncludeZeroStock && (
            <>
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={includeZeroStock}
                    onChange={(e) => setIncludeZeroStock(e.target.checked)}
                    disabled={generating}
                  />
                  <span className="checkmark"></span>
                  {t('dresses.priceListIncludeZeroStock', 'Include dresses with 0 stock count')}
                </label>
              </div>
              <p className="form-hint" style={{ marginTop: 0 }}>
                {t(
                  'dresses.exportCsvIncludeZeroStockHint',
                  'By default, dresses with zero stock are excluded from the export.'
                )}
              </p>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="salon-button secondary" onClick={onClose} disabled={generating}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="salon-button primary"
            onClick={handleDownload}
            disabled={generating}
          >
            {generating
              ? t('dresses.priceListGenerating', 'Generating...')
              : t('dresses.priceListDownload', 'Download PDF')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DownloadPriceListModal;
